import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  X, Search, Loader2, Calculator, Plus, Trash2, Pencil, Download, Mail, Clock,
  Building2, CheckCircle2, FilePlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import HOSupportAddFromDocumentModal from './HOSupportAddFromDocumentModal';
import HOSupportApplyModal from './HOSupportApplyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC_API = `${API}/order-book-reports/ho-support/calculator`;
const ORANGE = '#ee7203';
const OTHER_GROUP = 'Other / Unassigned';
const fieldCardClass = 'rounded-xl border border-gray-200 bg-white shadow-sm p-3';
const inputClass =
  'rounded-lg border-2 border-gray-300 bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 h-10';
const headerBtnClass =
  'inline-flex items-center gap-1.5 rounded-xl h-9 px-3 border-2 bg-white text-xs font-bold whitespace-nowrap transition-all shadow-sm hover:shadow-md active:scale-95';
const MODAL_MAX_W = 1100;
const RAIL_W = 40;

// ── Calculation — the cost steps are identical to Quotes.jsx calculateMarginCost + toggleHoSupportApply; the selling price is worked from the net cost WITHOUT H/O support (see computeLine). Kept identical to the backend (routes/ho_support_calculator.py compute_line) ──
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

export function computeLine(l) {
  const list = num(l.list_price);
  let mult = num(l.uom_multiplier, 1);
  if (mult <= 0) mult = 1;
  const disc = num(l.discount_pct);
  const reb = num(l.rebate_pct);
  const ho = num(l.ho_support_pct);
  const qty = num(l.quantity, 1);

  const afterUom = list * mult;
  const afterDiscount = afterUom * (1 - disc / 100);
  const hoAmount = r2((afterDiscount * ho) / 100);   // taken off list-less-discount, BEFORE rebate
  const afterRebate = afterDiscount * (1 - reb / 100);
  const netWithout = r2(afterRebate);
  const netWith = r2(afterRebate - hoAmount);

  // The selling price starts at the NET COST WITHOUT H/O SUPPORT: with nothing entered yet the contribution before H/O support
  // is £0 and the contribution after it is exactly the H/O support. Typing a contribution margin % or a selling price then
  // moves both figures. The margin % is the margin BEFORE H/O support (selling price = net cost without support / (1 - margin)).
  let sell;
  if (l.price_mode === 'selling') {
    sell = num(l.selling_price) > 0 ? r2(num(l.selling_price)) : netWithout;
  } else {
    const divisor = 1 - num(l.margin_pct) / 100;   // contribution margin: divide, not multiply
    sell = divisor > 0 ? r2(netWithout / divisor) : netWithout;
  }
  const cPre = r2(sell - netWithout);
  const cPost = r2(sell - netWith);
  const pct = (c) => (sell > 0 ? r2((c / sell) * 100) : 0);
  return {
    qty,
    afterUom: r2(afterUom),
    discountAmount: r2(afterUom - afterDiscount),
    afterDiscount: r2(afterDiscount),
    hoAmount,
    rebateAmount: r2(afterDiscount - afterRebate),
    afterRebate: r2(afterRebate),
    netWithout,
    netWith,
    sell,
    cPre,
    cPost,
    pctPre: pct(cPre),
    pctPost: pct(cPost),
    lineSell: r2(sell * qty),
    lineCPre: r2(cPre * qty),
    lineCPost: r2(cPost * qty),
    lineCostWithout: r2(netWithout * qty),
    lineCostWith: r2(netWith * qty),
    lineSupport: r2(hoAmount * qty),
  };
}

const groupBySupplier = (lines) => {
  const map = new Map();
  (lines || []).forEach((ln, index) => {
    const key = (ln.supplier_name || '').trim() || OTHER_GROUP;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push({ ln, index });
  });
  return [...map.entries()].sort((a, b) => {
    if ((a[0] === OTHER_GROUP) !== (b[0] === OTHER_GROUP)) return a[0] === OTHER_GROUP ? 1 : -1;
    return a[0].toLowerCase().localeCompare(b[0].toLowerCase());
  });
};

const gbp = (v) => `£${(Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pctText = (v) => `${(Number(v) || 0).toFixed(2)}%`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  } catch (_) { return iso; }
};
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const BLANK_LINE = {
  id: null, code: '', description: '', supplier_name: '', quantity: 1, unit: 'Each',
  list_price: '', uom_multiplier: 1, discount_pct: 0, rebate_pct: 0, ho_support_pct: 0,
  price_mode: 'margin', margin_pct: '', selling_price: '',
};

// ── Add / edit one product line ──────────────────────────────────────────────────────────────
function AddLineModal({ isOpen, initial, onClose, onSave }) {
  const [form, setForm] = useState(BLANK_LINE);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupNote, setLookupNote] = useState('');
  const seqRef = useRef(0);
  const skipSearchRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(initial ? { ...BLANK_LINE, ...initial } : BLANK_LINE);
    setQuery(initial?.code || '');
    setResults([]);
    setLookupNote('');
  }, [isOpen, initial]);

  // Debounced product search — a single word searches by product code, anything with a space by description
  useEffect(() => {
    if (!isOpen) return undefined;
    const q = query.trim();
    if (skipSearchRef.current) { skipSearchRef.current = false; setResults([]); return undefined; }
    if (q.length < 2 || (initial && q === (initial.code || ''))) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      const seq = ++seqRef.current;
      setSearching(true);
      try {
        const endpoint = q.includes(' ') ? 'search' : 'search-code';
        const res = await api.get(`${API}/stock-database/${endpoint}`, {
          params: { q, limit: 20, include_all: true, include_other_branches: false },
        });
        if (seq === seqRef.current) setResults(res.data?.results || []);
      } catch (_) {
        if (seq === seqRef.current) setResults([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, isOpen, initial]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const pickProduct = async (p) => {
    const code = String(p.product_code || p.code || '').trim().toUpperCase();
    const description = p.description || p.name || '';
    setResults([]);
    skipSearchRef.current = true;
    setQuery(code);
    setForm((f) => ({ ...f, code, description }));
    setLookupBusy(true);
    setLookupNote('');
    const [pricing, ho, cf] = await Promise.allSettled([
      api.get(`${API}/pricing/lookup/${encodeURIComponent(code)}`),
      api.get(`${API}/order-book-reports/ho-support/product-lookup`, { params: { code } }),
      api.get(`${API}/stock-database/cost-factor/${encodeURIComponent(code)}`),
    ]);
    const notes = [];
    setForm((f) => {
      const next = { ...f, code, description };
      const pr = pricing.status === 'fulfilled' ? pricing.value.data : null;
      const hs = ho.status === 'fulfilled' ? ho.value.data : null;
      const cfd = cf.status === 'fulfilled' ? cf.value.data : null;
      if (pr?.found) {
        const costFactor = cfd?.found ? parseFloat(cfd.cost_factor) : NaN;
        const needsCf = pr.quote_uom && pr.quote_uom !== 'Each' && costFactor > 0;
        next.list_price = pr.list_price;
        next.discount_pct = pr.discount_pct || 0;
        next.rebate_pct = pr.rebate_pct || 0;
        next.uom_multiplier = needsCf ? costFactor : 1;
        if (pr.supplier_name) next.supplier_name = pr.supplier_name;
        notes.push(`List price, discount and rebate from Settings > Pricing${pr.updated_label ? ` (updated ${pr.updated_label})` : ''}.`);
        if (pr.quote_uom && pr.quote_uom !== 'Each' && !needsCf) {
          notes.push(`The list price is per ${pr.quote_uom} and no cost factor was found — set the UOM multiplier by hand.`);
        } else if (needsCf) {
          notes.push(`List price is per ${pr.quote_uom}; multiplied by cost factor ${costFactor} to give the price per Each.`);
        }
      } else {
        notes.push('Not priced in Settings > Pricing — enter the list price, discount and rebate by hand.');
      }
      if (hs?.matched) {
        next.ho_support_pct = Math.round(hs.suggested_pct * 10000) / 100;
        if (!next.supplier_name && hs.supplier_code) next.supplier_name = hs.supplier_code;
        notes.push(`H/O support ${next.ho_support_pct}% suggested from the Supplier Matrix (${hs.supplier_code} ${hs.group}, higher end of ${hs.claim_range}).`);
      } else {
        notes.push('No H/O Support Matrix match for this product — enter the H/O support % by hand if it has one.');
      }
      return next;
    });
    setLookupNote(notes.join(' '));
    setLookupBusy(false);
  };

  const calc = useMemo(() => computeLine(form), [form]);
  const needsPrice = form.price_mode === 'selling' ? num(form.selling_price) <= 0 : false;

  const handleSave = () => {
    if (!form.code.trim() && !form.description.trim()) { toast.error('Pick a product or enter a code / description'); return; }
    if (num(form.list_price) <= 0) { toast.error('Enter a list price greater than zero'); return; }
    if (num(form.quantity, 0) <= 0) { toast.error('Quantity must be greater than zero'); return; }
    if (needsPrice) { toast.error('Enter a selling price'); return; }
    onSave({
      ...form,
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      supplier_name: form.supplier_name.trim(),
      quantity: num(form.quantity, 1),
      list_price: num(form.list_price),
      uom_multiplier: num(form.uom_multiplier, 1) > 0 ? num(form.uom_multiplier, 1) : 1,
      discount_pct: num(form.discount_pct),
      rebate_pct: num(form.rebate_pct),
      ho_support_pct: num(form.ho_support_pct),
      margin_pct: num(form.margin_pct),
      selling_price: num(form.selling_price),
    });
  };

  if (!isOpen) return null;

  const numField = (label, field, extra = {}) => (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">{label}</Label>
      <Input
        type="number"
        step="any"
        value={form[field]}
        onChange={(e) => set(field, e.target.value)}
        className={`mt-1 ${inputClass}`}
        data-testid={`ho-calc-line-${field}`}
        {...extra}
      />
    </div>
  );

  const stepRow = (label, value, opts = {}) => (
    <div className={`flex items-center justify-between px-3 py-1.5 text-sm ${opts.strong ? 'font-bold bg-orange-50' : ''} ${opts.border ? 'border-t border-gray-200' : ''}`}>
      <span className={opts.muted ? 'text-gray-500' : 'text-gray-800'}>{label}</span>
      <span className={`tabular-nums ${opts.negative ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-3" style={{ zIndex: 95 }} data-testid="ho-calc-line-overlay">
      <div className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl w-full border-2 border-orange-300 flex flex-col overflow-hidden" style={{ maxWidth: 900, maxHeight: '94vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
              <Calculator className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-gray-900 leading-tight">{initial ? 'Edit Line' : 'Add Line'}</h2>
              <p className="text-[11px] text-gray-500">Same calculation as a quote line</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md" data-testid="ho-calc-line-close" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          {/* Product */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Product code or description</Label>
            <div className="relative mt-1.5">
              <Input
                value={query}
                onChange={(e) => { setQuery(e.target.value); setForm((f) => ({ ...f, code: e.target.value.trim().toUpperCase() })); }}
                placeholder="Type a product code (or a description) to search…"
                className={`pr-9 ${inputClass}`}
                autoFocus
                data-testid="ho-calc-line-search"
              />
              {(searching || lookupBusy) ? (
                <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-gray-400" />
              ) : (
                <Search className="w-4 h-4 absolute right-3 top-3 text-gray-400" />
              )}
              {results.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 border border-gray-200 rounded-xl divide-y max-h-60 overflow-y-auto bg-white shadow-xl" style={{ zIndex: 5 }}>
                  {results.map((p, i) => (
                    <button
                      key={`${p.product_code || p.code}-${i}`}
                      type="button"
                      onClick={() => pickProduct(p)}
                      className="w-full text-left px-3 py-2 hover:bg-orange-50"
                      data-testid={`ho-calc-line-result-${i}`}
                    >
                      <div className="font-mono text-xs font-bold text-gray-900">{p.product_code || p.code}</div>
                      <div className="text-xs text-gray-600 truncate">{p.description || p.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-2">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Description</Label>
              <Input value={form.description} onChange={(e) => set('description', e.target.value)} className={`mt-1 ${inputClass}`} data-testid="ho-calc-line-description" />
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Supplier group (PDF is grouped by this)</Label>
                <Input value={form.supplier_name} onChange={(e) => set('supplier_name', e.target.value)} placeholder="e.g. Knauf" className={`mt-1 ${inputClass}`} data-testid="ho-calc-line-supplier" />
              </div>
              {numField('Quantity', 'quantity', { min: 0 })}
            </div>
            {lookupNote && <p className="mt-2 text-[11px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1.5" data-testid="ho-calc-line-note">{lookupNote}</p>}
          </div>

          {/* Inputs */}
          <div className={fieldCardClass}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {numField('List price £', 'list_price', { min: 0 })}
              {numField('UOM multiplier', 'uom_multiplier', { min: 0 })}
              {numField('Discount %', 'discount_pct')}
              {numField('H/O support %', 'ho_support_pct')}
              {numField('Rebate %', 'rebate_pct')}
            </div>
          </div>

          {/* Breakdown */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden" data-testid="ho-calc-line-breakdown">
            {stepRow('List price', gbp(num(form.list_price)))}
            {num(form.uom_multiplier, 1) !== 1 && stepRow(`× UOM multiplier ${num(form.uom_multiplier, 1)}`, gbp(calc.afterUom), { muted: true })}
            {stepRow(`Less discount ${pctText(num(form.discount_pct))}`, `−${gbp(calc.discountAmount)}`, { negative: true, muted: true })}
            {stepRow('Price after discount', gbp(calc.afterDiscount), { border: true })}
            {stepRow(`H/O support ${pctText(num(form.ho_support_pct))} of price after discount (before rebate)`, `−${gbp(calc.hoAmount)}`, { negative: true, muted: true })}
            {stepRow(`Less rebate ${pctText(num(form.rebate_pct))}`, `−${gbp(calc.rebateAmount)}`, { negative: true, muted: true })}
            {stepRow('NET COST WITHOUT H/O SUPPORT', gbp(calc.netWithout), { strong: true, border: true })}
            {stepRow('NET COST WITH H/O SUPPORT', gbp(calc.netWith), { strong: true })}
          </div>

          {/* Price mode */}
          <div className={fieldCardClass}>
            <div className="flex gap-2 mb-2">
              {[{ v: 'margin', l: 'Contribution margin %' }, { v: 'selling', l: 'Selling price £' }].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set('price_mode', o.v)}
                  className="flex-1 rounded-xl py-1.5 text-xs font-semibold border-2 transition-colors"
                  style={form.price_mode === o.v ? { background: ORANGE, borderColor: ORANGE, color: 'white' } : { background: 'white', borderColor: '#e5e7eb', color: '#6b7280' }}
                  data-testid={`ho-calc-line-mode-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
              {form.price_mode === 'margin'
                ? numField('Contribution margin % (before H/O support)', 'margin_pct')
                : numField('Selling price £ (per unit)', 'selling_price', { min: 0 })}
              <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <div className="text-[10px] font-bold uppercase text-gray-500">Selling price</div>
                  <div className="text-base font-extrabold text-gray-900 tabular-nums">{gbp(calc.sell)}</div>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <div className="text-[10px] font-bold uppercase text-gray-500">Line selling total</div>
                  <div className="text-base font-extrabold text-gray-900 tabular-nums">{gbp(calc.lineSell)}</div>
                </div>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-lg border-2 border-gray-300 bg-white px-3 py-2">
                <div className="text-[10px] font-bold uppercase text-gray-500">Contribution BEFORE H/O support</div>
                <div className="text-base font-extrabold tabular-nums text-gray-900">{gbp(calc.cPre)} <span className="text-sm font-semibold text-gray-500">({pctText(calc.pctPre)})</span></div>
                <div className="text-[11px] text-gray-500">Line total {gbp(calc.lineCPre)}</div>
              </div>
              <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2">
                <div className="text-[10px] font-bold uppercase text-emerald-700">Contribution AFTER H/O support</div>
                <div className="text-base font-extrabold tabular-nums text-emerald-800">{gbp(calc.cPost)} <span className="text-sm font-semibold text-emerald-700">({pctText(calc.pctPost)})</span></div>
                <div className="text-[11px] text-emerald-700">Line total {gbp(calc.lineCPost)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-orange-100 bg-white/60 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="rounded-xl h-10 px-4" data-testid="ho-calc-line-cancel">Cancel</Button>
          <Button onClick={handleSave} className="rounded-xl h-10 px-6 text-white font-semibold" style={{ backgroundColor: ORANGE }} data-testid="ho-calc-line-save">
            <Plus className="w-4 h-4 mr-1.5" />{initial ? 'Save Line' : 'Add Line'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── One product line as its own card: steps down the page with the amount beside each, contribution beside the final net costs ──
function LineCard({ ln, index, onEdit, onDelete }) {
  const c = computeLine(ln);
  const mult = num(ln.uom_multiplier, 1);
  const step = (label, value, o = {}) => (
    <div className={`flex items-center justify-between gap-3 px-3 py-1.5 text-[13px] ${o.border ? 'border-t border-gray-200' : ''} ${o.bold ? 'font-bold' : ''}`}>
      <span className={o.muted ? 'text-gray-500' : 'text-gray-800'}>{label}</span>
      <span className={`tabular-nums whitespace-nowrap ${o.negative ? 'text-red-600' : 'text-gray-900'}`}>{value}</span>
    </div>
  );
  return (
    <div className="rounded-2xl border-2 border-orange-300 bg-white shadow-sm overflow-hidden" data-testid={`ho-calc-line-card-${index}`}>
      <div className="flex items-start justify-between gap-2 px-3 py-2 text-white" style={{ backgroundColor: ORANGE }}>
        <div className="min-w-0">
          <div className="font-mono text-sm font-bold truncate">{ln.code || 'No code'}</div>
          <div className="text-[11px] opacity-95 leading-snug">{ln.description}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-[11px] font-bold bg-white/25 rounded-full px-2 py-0.5">Qty {c.qty}</span>
          <button onClick={onEdit} className="text-white/80 hover:text-white" title="Edit line" data-testid={`ho-calc-line-edit-${index}`}><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={onDelete} className="text-white/80 hover:text-white" title="Remove line" data-testid={`ho-calc-line-delete-${index}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      {step('List price', gbp(num(ln.list_price)))}
      {mult !== 1 && step(`× UOM multiplier ${mult}`, gbp(c.afterUom), { muted: true })}
      {step(`Less discount ${pctText(num(ln.discount_pct))}`, `-${gbp(c.discountAmount)}`, { muted: true, negative: true })}
      {step('Price after discount', gbp(c.afterDiscount), { border: true, bold: true })}
      {step(`H/O support ${pctText(num(ln.ho_support_pct))} of price after discount (before rebate)`, `-${gbp(c.hoAmount)}`, { muted: true, negative: true })}
      {step(`Less rebate ${pctText(num(ln.rebate_pct))}`, `-${gbp(c.rebateAmount)}`, { muted: true, negative: true })}

      <div className="grid grid-cols-5 border-t-2 border-orange-200 items-stretch">
        <div className="col-span-3 flex items-center justify-between gap-3 px-3 py-2 bg-orange-50">
          <span className="text-[12px] font-extrabold text-gray-900">NET COST WITHOUT H/O SUPPORT</span>
          <span className="text-sm font-extrabold tabular-nums">{gbp(c.netWithout)}</span>
        </div>
        <div className="col-span-2 px-3 py-2 border-l-2 border-orange-200 bg-white" data-testid={`ho-calc-line-contr-pre-${index}`}>
          <div className="text-[9px] font-bold uppercase text-gray-500 leading-tight">Contribution before H/O support</div>
          <div className="text-sm font-extrabold tabular-nums text-gray-900">{gbp(c.cPre)} <span className="text-xs font-semibold text-gray-500">({pctText(c.pctPre)})</span></div>
        </div>
      </div>
      <div className="grid grid-cols-5 border-t border-gray-200 items-stretch">
        <div className="col-span-3 flex items-center justify-between gap-3 px-3 py-2 bg-orange-50">
          <span className="text-[12px] font-extrabold text-gray-900">NET COST WITH H/O SUPPORT</span>
          <span className="text-sm font-extrabold tabular-nums">{gbp(c.netWith)}</span>
        </div>
        <div className="col-span-2 px-3 py-2 border-l-2 border-emerald-300 bg-emerald-50" data-testid={`ho-calc-line-contr-post-${index}`}>
          <div className="text-[9px] font-bold uppercase text-emerald-700 leading-tight">Contribution after H/O support</div>
          <div className="text-sm font-extrabold tabular-nums text-emerald-800">{gbp(c.cPost)} <span className="text-xs font-semibold text-emerald-700">({pctText(c.pctPost)})</span></div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-200">
        <span className="text-[12px] font-bold text-gray-800">
          SELLING PRICE <span className="font-normal text-gray-500">
            ({ln.price_mode === 'selling' ? 'entered' : `${pctText(num(ln.margin_pct))} contribution margin`})
          </span>
        </span>
        <span className="text-sm font-extrabold tabular-nums">{gbp(c.sell)}</span>
      </div>
      {c.qty !== 1 && (
        <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-600 tabular-nums">
          × {c.qty} units: selling {gbp(c.lineSell)} · contribution before {gbp(c.lineCPre)} · after {gbp(c.lineCPost)}
        </div>
      )}
    </div>
  );
}

// ── Main modal ──────────────────────────────────────────────────────────────────────────────────
export default function HOSupportCalculatorModal({ isOpen, onClose, user }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();

  const [view, setView] = useState('recents');            // 'recents' | 'customer' | 'detail'
  const [recents, setRecents] = useState([]);
  const [recentsLoading, setRecentsLoading] = useState(false);
  const [calc, setCalc] = useState(null);                  // { id, reference, customer, lines, status }
  const [accountQuery, setAccountQuery] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [lineModal, setLineModal] = useState({ open: false, index: null });
  const [saving, setSaving] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [emailOther, setEmailOther] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const loadRecents = async () => {
    setRecentsLoading(true);
    try {
      const res = await api.get(CALC_API);
      setRecents(res.data?.calculations || []);
    } catch (_) {
      setRecents([]);
      toast.error('Could not load recent calculations');
    } finally {
      setRecentsLoading(false);
    }
  };

  const resetAll = () => {
    setView('recents');
    setCalc(null);
    setAccountQuery('');
    setAccounts([]);
    setLineModal({ open: false, index: null });
    setFinishOpen(false);
    setEmailOther(false);
    setToEmail('');
    setMessage('');
    setBusy('');
  };

  useEffect(() => {
    if (isOpen) { resetAll(); loadRecents(); }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !modalUiV2) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen, modalUiV2]);

  useEffect(() => {
    if (!isOpen || !modalUiV2) return undefined;
    return activityBar.suppressBar();
  }, [isOpen, modalUiV2]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced CRM account search (same endpoint as Customer Management)
  useEffect(() => {
    if (!isOpen || view !== 'customer') return undefined;
    if (accountQuery.trim().length < 2) { setAccounts([]); return undefined; }
    const timer = setTimeout(() => runAccountSearch(accountQuery), 300);
    return () => clearTimeout(timer);
  }, [accountQuery, isOpen, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAccountSearch = async (q) => {
    if (!q?.trim()) return;
    setAccountLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-accounts`, { params: { q: q.trim() } });
      setAccounts(res.data?.accounts || []);
    } catch (_) {
      setAccounts([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const startNew = () => {
    setCalc(null);
    setAccountQuery('');
    setAccounts([]);
    setFinishOpen(false);
    setView('customer');
  };

  const selectAccount = async (account) => {
    let address = '';
    try {
      const res = await api.get(`${API}/zoho/accounts/${account.id}/fresh`);
      address = res.data?.address || '';
    } catch (_) { /* the calculation still works without an address */ }
    setCalc({
      id: null,
      reference: null,
      status: 'draft',
      lines: [],
      customer: { id: account.id, name: account.name, customer_code: account.customer_code || null, address },
    });
    setAccounts([]);
    setView('detail');
    setLineModal({ open: true, index: null });   // the Add Line modal opens straight away
  };

  const openRecent = async (item) => {
    try {
      const res = await api.get(`${CALC_API}/${item.id}`);
      setCalc(res.data);
      setFinishOpen(false);
      setView('detail');
    } catch (_) {
      toast.error('Could not open that calculation');
    }
  };

  const deleteRecent = async (item, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete the calculation for ${item.customer?.name || 'this customer'}?`)) return;
    try {
      await api.delete(`${CALC_API}/${item.id}`);
      setRecents((prev) => prev.filter((r) => r.id !== item.id));
    } catch (_) {
      toast.error('Failed to delete');
    }
  };

  // Saves the working calculation (auto-save on every line change and on Complete)
  const persist = async (next, status = 'draft') => {
    setSaving(true);
    try {
      const res = await api.post(CALC_API, { id: next.id || null, customer: next.customer, lines: next.lines, status });
      setCalc(res.data);
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the calculation');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLine = async (line) => {
    const lines = [...(calc?.lines || [])];
    if (lineModal.index === null) lines.push(line);
    else lines[lineModal.index] = line;
    setLineModal({ open: false, index: null });
    await persist({ ...calc, lines }, 'draft');
  };

  const handleDeleteLine = async (index) => {
    const lines = (calc?.lines || []).filter((_, i) => i !== index);
    if (lines.length === 0 && !calc?.id) { setCalc({ ...calc, lines }); return; }
    await persist({ ...calc, lines }, 'draft');
  };

  // Adds the lines picked in "Add from Quote / Order" to the calculation and saves it (the same auto-save Add Line uses).
  const handleAddDocumentLines = async (newLines, unpricedCount) => {
    const lines = [...(calc?.lines || []), ...newLines];
    const saved = await persist({ ...calc, lines }, 'draft');
    if (!saved) throw new Error('The calculation could not be saved');
    toast.success(`Added ${newLines.length} line${newLines.length === 1 ? '' : 's'}`);
    if (unpricedCount > 0) {
      toast.warning(
        `${unpricedCount} line${unpricedCount === 1 ? ' has' : 's have'} no list price in Settings > Pricing - click the pencil on ${unpricedCount === 1 ? 'it' : 'each'} to enter one before you complete the calculation`,
        { duration: 9000 },
      );
    }
  };

  const goRecents = () => {
    setFinishOpen(false);
    setView('recents');
    loadRecents();
  };

  const completeAnd = async (action) => {
    if (!calc?.lines?.length) { toast.error('Add at least one line first'); return; }
    const unpricedLines = (calc.lines || []).filter((ln) => !(num(ln.list_price) > 0));
    if (unpricedLines.length > 0) {
      const names = unpricedLines.map((ln) => ln.code || ln.description || 'a line').slice(0, 3).join(', ');
      toast.error(`Enter a list price for ${names}${unpricedLines.length > 3 ? ' and more' : ''} before completing`);
      return;
    }
    if (action === 'other') {
      if (!EMAIL_RE.test(toEmail.trim())) { toast.error('Enter a valid email address'); return; }
    }
    setBusy(action);
    const saved = await persist(calc, 'complete');
    if (!saved) { setBusy(''); return; }
    try {
      if (action === 'download') {
        const res = await api.get(`${CALC_API}/${saved.id}/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `HO_Support_${(saved.customer?.name || 'customer').replace(/[^a-zA-Z0-9]/g, '_')}_${saved.reference}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        const res = await api.post(`${CALC_API}/${saved.id}/email`, {
          mode: action === 'other' ? 'other' : 'self',
          to_email: action === 'other' ? toEmail.trim() : null,
          message: action === 'other' ? message.trim() : null,
        });
        toast.success(res.data?.message || 'Email sent');
      }
      goRecents();
    } catch (e) {
      let detail = 'Something went wrong';
      try {
        const data = e?.response?.data;
        detail = data instanceof Blob ? JSON.parse(await data.text()).detail : (data?.detail || detail);
      } catch (_) { /* keep default */ }
      toast.error(detail);
    } finally {
      setBusy('');
    }
  };

  const groups = useMemo(() => groupBySupplier(calc?.lines || []), [calc]);

  if (!isOpen) return null;

  const editingLine = lineModal.index !== null ? calc?.lines?.[lineModal.index] : null;
  const titleText = view === 'detail' && calc ? calc.customer?.name : 'H/O Support Calculator';
  const subText = view === 'recents'
    ? 'Recent calculations'
    : view === 'customer'
      ? 'Search an account to get started'
      : `H/O Support Calculator${calc?.reference ? ` — ${calc.reference}` : ''}`;

  return (
    <>
      {createPortal(
        <div
          className={`fixed left-0 right-0 bottom-0 flex items-stretch justify-center ${modalUiV2 ? 'p-3' : 'p-3 pt-8'} ${modalUiV2 ? 'bg-black/50' : 'bg-black/70 backdrop-blur-sm'}`}
          style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }}
          data-testid="ho-calc-overlay"
        >
          <div className="flex items-stretch justify-center w-full h-full min-w-0" data-testid="ho-calc-shell">
            {/* ── Calculator modal ── */}
            <div
              className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl h-full w-full border-2 border-orange-300 flex flex-col overflow-hidden min-w-0"
              style={{ maxWidth: MODAL_MAX_W }}
              data-testid="ho-calc-modal"
            >
              {/* Header */}
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0 flex-nowrap overflow-x-auto">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
                    <Calculator className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 tracking-tight leading-tight truncate max-w-[360px]">{titleText}</h2>
                    <p className="text-[11px] text-gray-500 truncate">{subText}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-nowrap justify-end ml-auto">
                  {view === 'recents' && (
                    <button onClick={startNew} className={`${headerBtnClass} border-orange-400 text-orange-700 hover:bg-orange-50`} data-testid="ho-calc-new-btn">
                      <FilePlus className="w-3.5 h-3.5" /> New Calculation
                    </button>
                  )}
                  {view !== 'recents' && (
                    <button onClick={goRecents} className={`md:hidden ${headerBtnClass} border-gray-300 text-gray-700 hover:bg-gray-50`} data-testid="ho-calc-recents-mobile-btn">
                      <Clock className="w-3.5 h-3.5" /> Recents
                    </button>
                  )}
                  {view === 'detail' && calc && (
                    <>
                      <button onClick={() => setLineModal({ open: true, index: null })} className={`${headerBtnClass} border-orange-400 text-orange-700 hover:bg-orange-50`} data-testid="ho-calc-add-line-btn">
                        <Plus className="w-3.5 h-3.5" /> Add Line
                      </button>
                      <button onClick={() => setDocModalOpen(true)} className={`${headerBtnClass} border-orange-400 text-orange-700 hover:bg-orange-50`} data-testid="ho-calc-add-from-doc-btn">
                        <FilePlus className="w-3.5 h-3.5" /> Add from Quote / Order
                      </button>
                      <button
                        onClick={() => setApplyOpen(true)}
                        disabled={!calc.id || !calc.lines?.length}
                        className={`${headerBtnClass} border-blue-400 text-blue-700 hover:bg-blue-50 disabled:opacity-50`}
                        title={calc.id ? "Add this calculation's support to New Claims and open the H/O Support form" : 'Add a line first so the calculation is saved'}
                        data-testid="ho-calc-apply-btn"
                      >
                        <Building2 className="w-3.5 h-3.5" /> Apply for H/O Support
                      </button>
                      <button
                        onClick={() => setFinishOpen((v) => !v)}
                        disabled={!calc.lines?.length}
                        className={`${headerBtnClass} border-emerald-400 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50`}
                        style={{ background: finishOpen ? '#d1fae5' : 'white' }}
                        data-testid="ho-calc-complete-btn"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => { resetAll(); onClose?.(); }}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md transition-colors flex-shrink-0"
                    data-testid="ho-calc-close-btn"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0 [overscroll-behavior:contain]">
                {/* ══ RECENTS ══ */}
                {view === 'recents' && (
                  <div className={fieldCardClass} data-testid="ho-calc-recents">
                    {recentsLoading ? (
                      <div className="text-sm text-gray-500 flex items-center gap-2 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                    ) : recents.length === 0 ? (
                      <div className="text-center py-10 text-gray-500 text-sm">
                        No calculations yet. Click <span className="font-bold text-orange-600">New Calculation</span> to start one.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {recents.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => openRecent(r)}
                            className="w-full text-left flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2.5 hover:bg-orange-50 hover:border-orange-300 transition-colors"
                            data-testid={`ho-calc-recent-${r.id}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-orange-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-gray-900 truncate">{r.customer?.name || 'Unnamed customer'}</div>
                              <div className="text-[11px] text-gray-500">{r.reference} · {fmtDate(r.updated_at)} · {r.line_count || 0} line{r.line_count === 1 ? '' : 's'}{r.claims_applied_at ? ' · Applied for H/O support' : ''}</div>
                            </div>
                            <span
                              className={`text-[11px] font-semibold px-2 py-1 rounded-full flex-shrink-0 ${r.status === 'complete' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-700'}`}
                            >
                              {r.status === 'complete' ? 'Complete' : 'Draft'}
                            </span>
                            <span onClick={(e) => deleteRecent(r, e)} className="text-gray-400 hover:text-red-600 p-1 flex-shrink-0" role="button" title="Delete" data-testid={`ho-calc-recent-delete-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ══ CUSTOMER SEARCH ══ */}
                {view === 'customer' && (
                  <div className={fieldCardClass}>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Search Account</Label>
                    <div className="flex gap-2 mt-1.5">
                      <Input
                        value={accountQuery}
                        onChange={(e) => { setAccountQuery(e.target.value); setAccounts([]); }}
                        placeholder="Type customer / company name…"
                        className={`flex-1 ${inputClass}`}
                        autoFocus
                        data-testid="ho-calc-account-search"
                      />
                      <Button type="button" onClick={() => runAccountSearch(accountQuery)} disabled={accountLoading} className="rounded-xl h-10 w-10 p-0" style={{ backgroundColor: ORANGE }}>
                        {accountLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
                      </Button>
                    </div>
                    {(accountLoading || accounts.length > 0) && (
                      <div className="border border-gray-200 rounded-xl divide-y max-h-72 overflow-y-auto mt-2 bg-white shadow-inner">
                        {accountLoading ? (
                          <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                        ) : accounts.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => selectAccount(a)}
                            className="w-full text-left p-3 hover:bg-orange-50 transition-colors"
                            data-testid={`ho-calc-account-option-${a.id}`}
                          >
                            <div className="font-semibold text-sm text-gray-900">{a.name}</div>
                            <div className="text-xs text-gray-500">{[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ══ DETAIL ══ */}
                {view === 'detail' && calc && (
                  <>
                    {finishOpen && (
                      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="ho-calc-finish-panel">
                        <div className="flex items-center gap-2 text-emerald-700 font-bold"><CheckCircle2 className="w-5 h-5" /> Complete this calculation</div>
                        <p className="text-xs text-gray-600">Completing saves it to Recents and sends the PDF where you choose.</p>
                        <div className="flex flex-wrap gap-2">
                          <Button onClick={() => completeAnd('download')} disabled={!!busy} className="rounded-xl h-10 px-4 text-white font-semibold" style={{ backgroundColor: ORANGE }} data-testid="ho-calc-download-btn">
                            {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} Download PDF
                          </Button>
                          <Button onClick={() => completeAnd('self')} disabled={!!busy} variant="outline" className="rounded-xl h-10 px-4 font-semibold" data-testid="ho-calc-email-self-btn">
                            {busy === 'self' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Email to me{user?.email ? ` (${user.email})` : ''}
                          </Button>
                          <Button onClick={() => setEmailOther((v) => !v)} disabled={!!busy} variant="outline" className="rounded-xl h-10 px-4 font-semibold" style={emailOther ? { borderColor: ORANGE, color: ORANGE } : undefined} data-testid="ho-calc-email-other-toggle">
                            <Mail className="w-4 h-4 mr-2" /> Email someone else
                          </Button>
                        </div>
                        {emailOther && (
                          <div className="space-y-2" data-testid="ho-calc-email-other-box">
                            <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="their.name@company.co.uk" type="email" className={inputClass} data-testid="ho-calc-email-to" />
                            <textarea
                              value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              placeholder="Message to include in the email (optional)…"
                              rows={3}
                              className="w-full border-2 border-gray-300 rounded-xl px-3 py-2 text-sm bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none resize-none"
                              data-testid="ho-calc-email-message"
                            />
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-gray-500">You will be copied in{user?.email ? ` (${user.email})` : ''}.</span>
                              <Button onClick={() => completeAnd('other')} disabled={!!busy} className="rounded-xl h-10 px-5 text-white font-semibold" style={{ backgroundColor: ORANGE }} data-testid="ho-calc-email-other-send">
                                {busy === 'other' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Send
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className={fieldCardClass}>
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Customer</span>
                        {calc.claims_applied_at && (
                          <span className="ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700" data-testid="ho-calc-applied-badge">
                            Applied for H/O support {fmtDate(calc.claims_applied_at)}
                          </span>
                        )}
                        {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400 ml-auto" />}
                      </div>
                      <div className="mt-1 text-sm font-bold text-gray-900">{calc.customer?.name}{calc.customer?.customer_code ? ` · #${calc.customer.customer_code}` : ''}</div>
                      {calc.customer?.address && <div className="text-xs text-gray-500 whitespace-pre-line">{calc.customer.address}</div>}
                    </div>

                    {(calc.lines || []).length === 0 ? (
                      <div className={`${fieldCardClass} text-center py-8 text-sm text-gray-500`}>
                        No lines yet. Click <span className="font-bold text-orange-600">Add Line</span> to add a product.
                      </div>
                    ) : (
                      groups.map(([groupName, rows]) => (
                        <div key={groupName} data-testid={`ho-calc-group-${groupName}`}>
                          <div className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-2 px-1">{groupName}</div>
                          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                            {rows.map(({ ln, index }) => (
                              <LineCard
                                key={ln.id || index}
                                ln={ln}
                                index={index}
                                onEdit={() => setLineModal({ open: true, index })}
                                onDelete={() => handleDeleteLine(index)}
                              />
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Recents tab — right-hand rail ── */}
            <div className="hidden md:flex flex-col gap-2 pt-20 flex-shrink-0" style={{ width: RAIL_W }} data-testid="ho-calc-tab-rail">
              <button
                type="button"
                onClick={goRecents}
                className={`flex flex-col items-center justify-center gap-2 py-4 rounded-r-2xl border-2 border-l-0 font-bold text-xs transition-colors ${
                  view === 'recents' ? 'text-white border-orange-500' : 'text-orange-700 bg-orange-50 border-orange-200 hover:bg-orange-100'
                }`}
                style={{ width: RAIL_W, ...(view === 'recents' ? { backgroundColor: ORANGE } : {}) }}
                title="Recents"
                data-testid="ho-calc-tab-recents"
              >
                <Clock className="w-4 h-4" />
                <span style={{ writingMode: 'vertical-rl', letterSpacing: '0.06em' }}>Recents</span>
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <HOSupportAddFromDocumentModal
        isOpen={docModalOpen && !!calc}
        customer={calc?.customer}
        onClose={() => setDocModalOpen(false)}
        onAddLines={handleAddDocumentLines}
      />

      <HOSupportApplyModal
        isOpen={applyOpen && !!calc?.id}
        calcId={calc?.id}
        onClose={() => setApplyOpen(false)}
        onApplied={(at) => setCalc((prev) => (prev ? { ...prev, claims_applied_at: at || new Date().toISOString() } : prev))}
      />

      <AddLineModal
        isOpen={lineModal.open && !!calc}
        initial={editingLine}
        onClose={() => setLineModal({ open: false, index: null })}
        onSave={handleSaveLine}
      />
    </>
  );
}
